// PopupInquiry.jsx - Fixed Data Format
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import api from '../services/api';
import './PopupInquiry.css';

const PopupInquiry = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: '',
    requirements: '',
    budget: '',
    timeline: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [step, setStep] = useState(1);
  
  const timerRef = useRef(null);
  const hasOpenedRef = useRef(false);
  const closeCountRef = useRef(0);

  // Check if popup should be shown on mount
  useEffect(() => {
    const checkShouldShow = () => {
      const hasSubmitted = localStorage.getItem('inquirySubmitted');
      const closeCount = parseInt(localStorage.getItem('popupCloseCount') || '0');
      const maxClosed = localStorage.getItem('popupMaxClosed');
      
      console.log('🔍 Popup check:', { hasSubmitted, closeCount, maxClosed });
      
      if (hasSubmitted === 'true') {
        console.log('❌ User already submitted, hiding popup');
        setShouldRender(false);
        return false;
      }
      
      if (maxClosed === 'true' || closeCount >= 2) {
        console.log('❌ User closed popup 2+ times, hiding popup');
        setShouldRender(false);
        return false;
      }
      
      console.log('✅ Popup should show');
      return true;
    };

    const shouldShow = checkShouldShow();
    
    if (!shouldShow) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const savedCloseCount = localStorage.getItem('popupCloseCount');
    if (savedCloseCount) {
      closeCountRef.current = parseInt(savedCloseCount);
    }

    let delay = 3000;
    
    if (closeCountRef.current === 1) {
      delay = 80000;
    }

    console.log(`⏰ Setting popup timer for ${delay}ms`);

    timerRef.current = setTimeout(() => {
      console.log('⏰ Timer fired, opening popup');
      if (!hasOpenedRef.current) {
        setIsOpen(true);
        hasOpenedRef.current = true;
      }
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      // ✅ Match the exact format from ContactSection
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        company: '', // Optional field
        service: formData.businessType || 'other', // ✅ businessType maps to service
        message: formData.requirements || 'Not specified',
        budget: formData.budget || 'not-specified',
        timeline: formData.timeline || 'not-specified',
        source: 'popup'
      };

      console.log('📤 POST http://localhost:5025/api/contacts/submit');
      console.log('📦 Data:', submissionData);
      
      // ✅ Direct API call
      const response = await api.post('/contacts/submit', submissionData);

      console.log('✅ Success:', response.data);

      setMessage({
        text: response.data.message || 'Thank you! We\'ll contact you within 24 hours.',
        type: 'success'
      });

      localStorage.setItem('inquirySubmitted', 'true');
      localStorage.removeItem('popupCloseCount');
      localStorage.removeItem('popupMaxClosed');
      closeCountRef.current = 0;

      setFormData({
        name: '',
        email: '',
        phone: '',
        businessType: '',
        requirements: '',
        budget: '',
        timeline: ''
      });

      setTimeout(() => {
        setIsOpen(false);
        setMessage({ text: '', type: '' });
        setStep(1);
        hasOpenedRef.current = false;
        setShouldRender(false);
      }, 3000);

    } catch (error) {
      console.error('❌ Error:', error);
      
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Cannot connect to server. Please ensure backend is running on port 5025.';
      } else if (error.response) {
        console.error('Error response:', error.response.data);
        // ✅ Show the actual validation error from backend
        if (error.response.data.errors) {
          const errors = error.response.data.errors;
          errorMessage = errors.map(err => err.msg || err.message).join(', ');
        } else {
          errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        }
      }
      
      setMessage({ text: errorMessage, type: 'error' });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const newCloseCount = closeCountRef.current + 1;
    closeCountRef.current = newCloseCount;
    
    localStorage.setItem('popupCloseCount', newCloseCount.toString());
    
    if (newCloseCount >= 2) {
      localStorage.setItem('popupMaxClosed', 'true');
      setShouldRender(false);
    }
    
    setIsOpen(false);
    setStep(1);
    setMessage({ text: '', type: '' });
    
    setTimeout(() => {
      hasOpenedRef.current = false;
    }, 1000);
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        setMessage({ text: 'Please fill in all required fields', type: 'error' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setMessage({ text: 'Please enter a valid email address', type: 'error' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        return;
      }
      
      setStep(2);
    }
  };

  const prevStep = () => setStep(1);

  const businessTypes = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'mobile-apps', label: 'Mobile App Development' },
    { value: 'ecommerce', label: 'E-commerce Website' },
    { value: 'data-analytics', label: 'Data Analytics' },
    { value: 'ai-ml', label: 'AI/ML Solutions' },
    { value: 'cloud-solutions', label: 'Cloud Solutions' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: '<10k', label: 'Less than ₹10k' },
    { value: '10k-25k', label: '₹10k - ₹25k' },
    { value: '25k-50k', label: '₹25k - ₹50k' },
    { value: '50k-100k', label: '₹50k - ₹100k' },
    { value: '100k+', label: '₹100k+' },
    { value: 'not-specified', label: 'Not Specified' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (ASAP)' },
    { value: '1-3-months', label: '1-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6+months', label: '6+ Months' },
    { value: 'not-specified', label: 'Not Specified' }
  ];

  if (!shouldRender) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="popup-container"
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="popup-close" onClick={handleClose} disabled={loading}>
              <FaTimes />
            </button>

            {/* Progress Bar */}
            <div className="popup-progress">
              <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">Basic Info</span>
              </div>
              <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
              <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Project Details</span>
              </div>
            </div>

            {/* Header */}
            <div className="popup-header">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="popup-icon"
              >
                🚀
              </motion.div>
              <h2>Need a Software Solution?</h2>
              <p>Get a free consultation and quote for your project</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="popup-form">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="form-step"
                  >
                    <div className="form-group">
                      <label>Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter Your Full Name"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter Your E-mail"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter Your Phone Number"
                        required
                        disabled={loading}
                      />
                    </div>

                    <motion.button
                      type="button"
                      className="next-btn"
                      onClick={nextStep}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={loading}
                    >
                      Next Step <FaArrowRight />
                    </motion.button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="form-step"
                  >
                    <div className="form-group">
                      <label>What type of project? *</label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select project type</option>
                        {businessTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Budget Range</label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          disabled={loading}
                        >
                          <option value="">Select budget</option>
                          {budgetOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Timeline</label>
                        <select
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleChange}
                          disabled={loading}
                        >
                          <option value="">Select timeline</option>
                          {timelineOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Project Requirements</label>
                      <textarea
                        name="requirements"
                        value={formData.requirements}
                        onChange={handleChange}
                        placeholder="Tell us briefly about your project requirements..."
                        rows="3"
                        disabled={loading}
                      ></textarea>
                    </div>

                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="back-btn" 
                        onClick={prevStep}
                        disabled={loading}
                      >
                        Back
                      </button>
                      <motion.button
                        type="submit"
                        className="submit-btn"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-small"></span>
                            Submitting...
                          </>
                        ) : (
                          'Submit'
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {message.text && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`popup-message ${message.type}`}
                >
                  {message.text}
                </motion.div>
              )}
            </form>

            {/* Footer */}
            <div className="popup-footer">
              <p>✨ No spam. We'll get back to you within 24 hours</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PopupInquiry;